'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import type ReactPlayerInstance from 'react-player';
import { PlayIcon } from '@heroicons/react/24/solid';
import { twJoin } from 'tailwind-merge';
import {
  ImageContentFragment,
  ImageFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { buildTrackingId } from '@/utils/tracking-helper';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { VideoMediaEvents } from './use-video-media-events';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

// The player is only mounted once playback is wanted, so the browser must not preload either.
const PLAYER_CONFIG = { file: { attributes: { preload: 'none' } } };

/** Half the viewport on desktop, the full width below - what the 50:50 poster needs. */
export const DEFAULT_POSTER_SIZES = '(max-width: 640px) 100vw, 50vw';

export interface VideoPosterLayerProps {
  poster?: ImageFragment | null;
  posterComponent?: ImageContentFragment | null;
  isHidden: boolean;
  /** Tiles that render the poster much smaller than half the viewport override this. */
  sizes?: string;
}

export function VideoPosterLayer({
  poster,
  posterComponent,
  isHidden,
  sizes = DEFAULT_POSTER_SIZES,
}: Readonly<VideoPosterLayerProps>) {
  if (!poster && !posterComponent) {
    return null;
  }

  return (
    <div
      className={twJoin(
        'pointer-events-none absolute inset-0 transition-opacity duration-500',
        isHidden ? 'opacity-0' : 'opacity-100',
      )}
    >
      <OptimizedImage
        image={poster ?? undefined}
        imageComponent={poster ? undefined : (posterComponent ?? undefined)}
        fill
        className="object-cover"
        sizes={sizes}
      />
    </div>
  );
}

export interface VideoMediaElementProps {
  src: string;
  playing: boolean;
  muted: boolean;
  loop?: boolean;
  events: VideoMediaEvents;
  onReady: (player: ReactPlayerInstance) => void;
}

/**
 * In react-player v2 `className` lands on the wrapper `div`; the global `.video-cover video` rule
 * makes the inner element cover it.
 */
export function VideoMediaElement({
  src,
  playing,
  muted,
  loop,
  events,
  onReady,
}: Readonly<VideoMediaElementProps>) {
  return (
    <ReactPlayer
      url={src}
      playing={playing}
      muted={muted}
      loop={loop}
      playsinline
      controls={false}
      width="100%"
      height="100%"
      className="video-cover h-full w-full"
      config={PLAYER_CONFIG}
      onReady={onReady}
      onPlay={events.onPlay}
      onPause={events.onPause}
      onStart={events.onStart}
      onEnded={events.onEnded}
      onError={events.onError}
    />
  );
}

export interface VideoIntentButtonProps {
  label: string;
  onClick: () => void;
  trackingId?: string;
}

/** The click affordance shown while the player itself is still unmounted. */
export function VideoIntentButton({
  label,
  onClick,
  trackingId,
}: Readonly<VideoIntentButtonProps>) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-tile-element-id={buildTrackingId(trackingId, 'play')}
      className="absolute inset-0 z-10 flex items-center justify-center text-white"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100/60">
        <PlayIcon className="h-6 w-6" aria-hidden="true" />
      </span>
    </button>
  );
}

interface VideoTapTargetProps {
  label: string;
  onClick: () => void;
}

/** Full-surface tap target used on touch devices, where the overlay stays visible. */
function VideoTapTarget({ label, onClick }: Readonly<VideoTapTargetProps>) {
  return (
    <button type="button" onClick={onClick} aria-label={label} className="absolute inset-0 z-10" />
  );
}

export interface VideoInteractionLayerProps {
  isPlayerMounted: boolean;
  isInteractive: boolean;
  overlayAlwaysVisible?: boolean;
  playing: boolean;
  playLabel: string;
  pauseLabel: string;
  onToggle: () => void;
  trackingId?: string;
}

/** Whatever the user clicks to start or stop the video, depending on the current phase. */
export function VideoInteractionLayer({
  isPlayerMounted,
  isInteractive,
  overlayAlwaysVisible,
  playing,
  playLabel,
  pauseLabel,
  onToggle,
  trackingId,
}: Readonly<VideoInteractionLayerProps>) {
  if (!isInteractive) {
    return null;
  }

  if (!isPlayerMounted) {
    return <VideoIntentButton label={playLabel} onClick={onToggle} trackingId={trackingId} />;
  }

  if (!overlayAlwaysVisible) {
    return null;
  }

  return <VideoTapTarget label={playing ? pauseLabel : playLabel} onClick={onToggle} />;
}

export interface VideoOverlayProps {
  isVisible: boolean;
  children: React.ReactNode;
}

const OVERLAY_VISIBLE_CLASSES =
  'opacity-100 [&_a]:pointer-events-auto [&_button]:pointer-events-auto';

/**
 * A faded-out link would still swallow clicks meant for the video, so the overlay content only
 * becomes clickable again together with the hover or the focus that brings it back on screen.
 */
const OVERLAY_HIDDEN_CLASSES = twJoin(
  'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
  'group-hover:[&_a]:pointer-events-auto',
  'group-hover:[&_button]:pointer-events-auto',
  'group-focus-within:[&_a]:pointer-events-auto',
  'group-focus-within:[&_button]:pointer-events-auto',
);

export function VideoOverlay({ isVisible, children }: Readonly<VideoOverlayProps>) {
  return (
    <div
      data-testid="video-player-overlay"
      className={twJoin(
        'pointer-events-none absolute inset-0 z-20 transition-opacity duration-300',
        // Text and links leave while the video runs; hovering or tabbing in brings them back.
        isVisible ? OVERLAY_VISIBLE_CLASSES : OVERLAY_HIDDEN_CLASSES,
      )}
    >
      {children}
    </div>
  );
}
